import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const contactFormUrl = 'http://zbpost.cn/';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch(contactFormUrl || '#', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网站是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫网站提供免费下拉式阅读体验。所有漫画均可免费在线观看，无需付费订阅。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫在线观看需要下载APP吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。韩漫网站是纯网页版平台，通过浏览器即可直接访问和阅读，支持手机和电脑端。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网站每日更新热门漫画，确保读者能够及时追更。不同作品的更新频率由原作者决定，热门作品通常1-3天更新一话。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在手机上阅读韩漫？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '只需在手机浏览器中打开韩漫网站即可。我们采用智能预加载和下拉式阅读设计，在手机上也能获得流畅的阅读体验。',
        },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网站',
    url: 'http://zbpost.cn/',
    description: '韩漫在线观看，免费漫画下拉式阅读平台',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description="韩漫网站致力于为全球中文读者提供优质的韩漫在线观看体验。了解我们的品牌故事、用户口碑评价，以及联系方式。免费漫画下拉式阅读，看韩漫就上韩漫网站。"
        keywords="韩漫网站, 关于我们, 韩漫在线观看, 联系韩漫"
        canonicalUrl="/about"
        ogType="website"
        schema={{ '@context': 'https://schema.org', '@graph': [orgSchema, faqSchema] }}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Intro */}
          <section className="py-8 md:py-12">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider mb-3">品牌故事</p>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-5">
                关于韩漫网站
              </h1>
              <div className="w-16 h-1 bg-primary-500 mx-auto mb-6 rounded-full"></div>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                韩漫网站诞生于一群韩漫爱好者的热情之中。我们发现许多中文读者在寻找优质韩漫阅读平台时面临种种不便——加载缓慢、画质模糊、操作繁琐。于是我们决定打造一个真正为读者着想的<strong>韩漫在线观看</strong>平台。
              </p>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mt-4">
                采用<strong>免费漫画下拉式</strong>阅读体验配合<strong>智能预加载</strong>技术，我们确保无论你在何种网络环境下，都能享受极速流畅的阅读。这就是<strong>漫画韩漫网址</strong>存在的意义——让每一位韩漫爱好者都能便捷地享受阅读乐趣。
              </p>
            </div>
          </section>

          {/* Features */}
          <section className="py-8 md:py-10 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                为什么选择<strong>韩漫网站</strong>？
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { icon: 'ri-flashlight-line', title: '极速加载', desc: '智能预加载技术，翻页无需等待' },
                  { icon: 'ri-smartphone-line', title: '多端适配', desc: '手机电脑平板，随时随地畅读' },
                  { icon: 'ri-image-line', title: '高清画质', desc: '原画级清晰度，细节纤毫毕现' },
                  { icon: 'ri-refresh-line', title: '每日更新', desc: '热门作品每日更新，追更不等待' },
                ].map((feat) => (
                  <div key={feat.title} className="p-5 bg-background-50 rounded-lg text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-1.5">{feat.title}</h3>
                    <p className="text-xs text-foreground-500">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                读者<strong>口碑</strong>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {[
                  { name: '漫画迷小王', text: '「韩漫在线观看体验真的绝了！下拉式阅读太方便了，而且加载超快。每天通勤路上必刷。」' },
                  { name: '追更达人', text: '「对比过好几个平台，韩漫网站的更新速度和画质都是最好的。已经安利给身边所有朋友了。」' },
                  { name: '资深宅女', text: '「漫画种类丰富，分类清晰，找漫超方便。界面也很简洁，没有乱七八糟的广告，阅读体验一级棒。」' },
                  { name: '新晋韩漫粉', text: '「刚开始看韩漫，朋友推荐的韩漫网站。免费就能看到这么多正版作品，太惊喜了！已经沉迷了。」' },
                ].map((item) => (
                  <div key={item.name} className="p-5 md:p-6 bg-background-100 rounded-lg flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent-700">{item.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-2 font-heading italic">{item.text}</p>
                      <p className="text-xs font-semibold text-foreground-900">{item.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-7">
                有任何问题或建议？欢迎联系我们，我们会在24小时内回复
              </p>

              {formStatus === 'success' ? (
                <div className="text-center py-12 bg-background-50 rounded-lg">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">发送成功！</h3>
                  <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-6 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 rounded-lg text-sm text-foreground-900 border border-background-200 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 rounded-lg text-sm text-foreground-900 border border-background-200 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 rounded-lg text-sm text-foreground-900 border border-background-200 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-100 rounded-lg text-sm text-foreground-900 border border-background-200 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>

                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                  {formStatus === 'error' && formError && (
                    <div className="mb-4 p-3 bg-primary-50 text-primary-700 text-sm rounded-lg">
                      <i className="ri-error-warning-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* FAQ */}
          <section className="py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  { q: '韩漫网站是否免费？', a: '是的，韩漫网站提供免费下拉式阅读体验。所有漫画均可免费在线观看，无需付费订阅。' },
                  { q: '韩漫在线观看需要下载APP吗？', a: '不需要。韩漫网站是纯网页版平台，通过浏览器即可直接访问和阅读，支持手机和电脑端。' },
                  { q: '漫画更新频率如何？', a: '韩漫网站每日更新热门漫画，确保读者能够及时追更。不同作品的更新频率由原作者决定，热门作品通常1-3天更新一话。' },
                  { q: '如何在手机上阅读韩漫？', a: '只需在手机浏览器中打开韩漫网站即可。我们采用智能预加载和下拉式阅读设计，在手机上也能获得流畅的阅读体验。' },
                  { q: '漫画画质如何？', a: '我们提供高清原画级别的漫画画质，确保每一处细节都纤毫毕现，带给你最佳的视觉享受。' },
                ].map((item, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors">
                      {item.q}
                      <i className="ri-arrow-down-s-line text-lg w-5 h-5 flex items-center justify-center group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}